import {
  Box,
  Container,
  Typography,
  Button,
  Card,
  styled,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Stack,
  Chip,
} from '@mui/material';
import React from 'react';
import { Link as RouterLink } from 'react-router-dom';

const SectionWrapper = styled(Box)(({ theme }) => ({
  padding: '96px 0',
  backgroundColor: theme.palette.background.paper,
}));

const SectionTitle = styled(Typography)(({ theme }) => ({
  fontFamily: '"Nohemi", sans-serif !important',
  fontWeight: 600,
  fontSize: '48px',
  lineHeight: '1.25em',
  letterSpacing: '-2%',
  color: '#181D27',
  textAlign: 'center',
  marginBottom: theme.spacing(2),
  [theme.breakpoints.down('md')]: {
    fontSize: '36px',
  },
}));

const SectionSubtitle = styled(Typography)(({ theme }) => ({
  fontFamily: 'Inter, sans-serif',
  fontWeight: 400,
  fontSize: '20px',
  lineHeight: '1.5em',
  color: '#535862',
  textAlign: 'center',
  marginBottom: theme.spacing(6),
}));

const SubheaderText = styled(Typography)(({ theme }) => ({
  fontFamily: 'Inter, sans-serif',
  fontWeight: 600,
  fontSize: '16px',
  lineHeight: '1.5em',
  color: theme.palette.primary.main,
  textAlign: 'center',
  marginBottom: theme.spacing(1),
}));

interface PlanCardProps {
  isPrimary?: boolean;
}

const PlanCard = styled(Card)<PlanCardProps>(({ theme, isPrimary }) => ({
  height: '100%',
  borderRadius: '8px',
  border: isPrimary ? `2px solid ${theme.palette.primary.main}` : 'none',
  overflow: 'visible',
  display: 'flex',
  flexDirection: 'column',
  transition: 'transform 0.3s ease, box-shadow 0.3s ease',
  '&:hover': {
    transform: 'translateY(-5px)',
    boxShadow: '0 12px 20px rgba(0, 0, 0, 0.1)',
  },
}));

const PlanHeader = styled(Box)(({ theme }) => ({
  padding: '16px 24px 16px',
  borderBottom: '1px solid #E9EAEB',
  display: 'flex',
  alignItems: 'center',
  gap: '8px',
}));

const PlanTitle = styled(Typography)(({ theme }) => ({
  fontFamily: 'Inter, sans-serif',
  fontWeight: 600,
  fontSize: '20px',
  lineHeight: '1.5em',
  color: '#181D27',
}));

const PlanContent = styled(Box)(({ theme }) => ({
  padding: '24px 20px 20px',
  display: 'flex',
  flexDirection: 'column',
  height: '100%',
}));

const PlanPrice = styled(Typography)(({ theme }) => ({
  fontFamily: '"Nohemi", sans-serif !important',
  fontWeight: 600,
  fontSize: '42px',
  lineHeight: '1.25em',
  letterSpacing: '-2%',
  color: '#181D27',
  display: 'inline-block',
}));

const PlanPeriod = styled(Typography)(({ theme }) => ({
  fontFamily: 'Inter, sans-serif',
  fontWeight: 500,
  fontSize: '16px',
  lineHeight: '1.5em',
  color: '#535862',
  marginLeft: '4px',
  display: 'inline-block',
}));

const PlanDescription = styled(Typography)(({ theme }) => ({
  fontFamily: 'Inter, sans-serif',
  fontWeight: 400,
  fontSize: '14px',
  lineHeight: '1.43em',
  color: '#535862',
  minHeight: '60px',
}));

const GetStartedButton = styled(Button)(({ theme }) => ({
  backgroundColor: theme.palette.primary.main,
  color: '#FFFFFF',
  borderRadius: '8px',
  fontWeight: 600,
  fontSize: '16px',
  padding: '12px 18px',
  textTransform: 'none',
  width: '100%',
  '&:hover': {
    backgroundColor: theme.palette.primary.dark,
  },
}));

const ChatToSalesButton = styled(Button)(({ theme }) => ({
  backgroundColor: 'transparent',
  color: '#414651',
  borderRadius: '8px',
  fontWeight: 600,
  fontSize: '16px',
  padding: '12px 18px',
  textTransform: 'none',
  border: '1px solid #D5D7DA',
  width: '100%',
  '&:hover': {
    backgroundColor: '#F4F6F8',
  },
}));

const ComparisonTable = styled(TableContainer)(({ theme }) => ({
  marginTop: theme.spacing(6),
  overflow: 'auto',
}));

interface StyledTableCellProps {
  isCategory?: boolean;
  isFeature?: boolean;
}

const StyledTableCell = styled(TableCell)<StyledTableCellProps>(
  ({ theme, isCategory, isFeature }) => {
    // Extract the color logic to avoid nested ternary
    let textColor = '#535862'; // Default color
    if (isCategory) {
      textColor = theme.palette.primary.main;
    } else if (isFeature) {
      textColor = '#181D27';
    }

    return {
      padding: '16px 24px',
      fontFamily: 'Inter, sans-serif',
      fontWeight: isCategory ? 600 : 400,
      fontSize: '14px',
      lineHeight: '1.43em',
      color: textColor,
      backgroundColor: isFeature ? '#FAFAFA' : 'transparent',
      borderBottom: '1px solid #E9EAEB',
    };
  }
);

const PopularBadge = styled(Chip)(({ theme }) => ({
  backgroundColor: 'rgba(157, 21, 0, 0.1)',
  color: theme.palette.primary.main,
  fontFamily: 'Inter, sans-serif',
  fontWeight: 500,
  fontSize: '14px',
  lineHeight: '1.43em',
  height: '24px',
  border: `1px solid ${theme.palette.primary.main}`,
  borderRadius: '16px',
}));

// Move component definitions outside of PricingPlansSection
const CellCheck: React.FC = () => (
  <Box sx={{ color: '#079455', display: 'flex', justifyContent: 'center' }}>
    <svg width="22" height="22" viewBox="0 0 22 22" fill="none" xmlns="http://www.w3.org/2000/svg">
      <path
        d="M11 21C16.5228 21 21 16.5228 21 11C21 5.47715 16.5228 1 11 1C5.47715 1 1 5.47715 1 11C1 16.5228 5.47715 21 11 21Z"
        stroke="#079455"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
      <path
        d="M8 11L10 13L14 9"
        stroke="#079455"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  </Box>
);

const CellMinus: React.FC = () => (
  <Box sx={{ color: '#A4A7AE', display: 'flex', justifyContent: 'center' }}>
    <svg width="22" height="22" viewBox="0 0 22 22" fill="none" xmlns="http://www.w3.org/2000/svg">
      <path
        d="M7 11H15"
        stroke="#A4A7AE"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  </Box>
);

const PricingPlansSection: React.FC = () => {
  const plans = [
    {
      name: 'Free',
      popular: false,
      price: 0,
      unitPrice: 0,
      units: 'Limited Use',
      description: 'Basic functionality for getting started with property management.',
      features: {
        users: '1',
        storage: '2 GB',
        support: true,
        integrations: false,
        analytics: 'Basic',
        apiAccess: false,
        inboundMessages: true,
        inboundSMS: false,
        inboundCalls: false,
        workflows: false,
        propertyInvoices: false,
        propertyCalendar: false,
        maintenanceRequests: false,
        documentVault: true,
      },
    },
    {
      name: 'Starter',
      popular: false,
      price: 15.99,
      unitPrice: 3.2,
      units: '1-5 Units',
      description: 'Essential features for property managers with up to 5 units.',
      features: {
        users: '2',
        storage: '5 GB',
        support: true,
        integrations: false,
        analytics: 'Basic',
        apiAccess: false,
        inboundMessages: true,
        inboundSMS: true,
        inboundCalls: true,
        workflows: false,
        propertyInvoices: true,
        propertyCalendar: true,
        maintenanceRequests: true,
        documentVault: true,
      },
    },
    {
      name: 'Basic',
      popular: false,
      price: 25.99,
      unitPrice: 1.3,
      units: '6-20 Units',
      description: 'Advanced features for growing property portfolios with up to 20 units.',
      features: {
        users: '3',
        storage: '20 GB',
        support: true,
        integrations: true,
        analytics: 'Standard',
        apiAccess: false,
        inboundMessages: true,
        inboundSMS: true,
        inboundCalls: true,
        workflows: false,
        propertyInvoices: true,
        propertyCalendar: true,
        maintenanceRequests: true,
        documentVault: true,
      },
    },
    {
      name: 'Growth',
      popular: false,
      price: 35.99,
      unitPrice: 0.72,
      units: '21-50 Units',
      description: 'Enhanced functionality for larger portfolios with up to 50 units.',
      features: {
        users: '5',
        storage: '50 GB',
        support: true,
        integrations: true,
        analytics: 'Advanced',
        apiAccess: true,
        inboundMessages: true,
        inboundSMS: true,
        inboundCalls: true,
        workflows: true,
        propertyInvoices: true,
        propertyCalendar: true,
        maintenanceRequests: true,
        documentVault: true,
      },
    },
    {
      name: 'Professional',
      popular: false,
      price: 50.99,
      unitPrice: 0.51,
      units: '51-100 Units',
      description: 'Premium features for professional property managers with up to 100 units.',
      features: {
        users: '20',
        storage: '100 GB',
        support: true,
        integrations: true,
        analytics: 'Advanced',
        apiAccess: true,
        inboundMessages: true,
        inboundSMS: true,
        inboundCalls: true,
        workflows: true,
        propertyInvoices: true,
        propertyCalendar: true,
        maintenanceRequests: true,
        documentVault: true,
      },
    },
  ];

  return (
    <SectionWrapper id="pricing-plans">
      <Container maxWidth="lg">
        {/* Header Section */}
        <Stack spacing={2} sx={{ mb: 6, textAlign: 'center' }}>
          <SubheaderText>Pricing</SubheaderText>
          <SectionTitle>Explore our plans and choose the one that fits you best.</SectionTitle>
          <SectionSubtitle>Simple, transparent pricing that grows with you.</SectionSubtitle>
        </Stack>

        {/* Pricing Plans */}
        <Stack
          direction="column"
          spacing={3}
          sx={{
            mb: 6,
            justifyContent: 'center',
            p: 3,
            backgroundColor: '#FAFAFA',
            borderRadius: '8px',
            border: '1px solid #E9EAEB',
          }}
        >
          {/* Top row - first 3 plans */}
          <Stack
            direction={{ xs: 'column', md: 'row' }}
            spacing={3}
            sx={{
              justifyContent: 'center',
            }}
          >
            {plans.slice(0, 3).map(plan => (
              <Box
                key={plan.name}
                sx={{
                  flex: { xs: '1 1 100%', md: '1 0 calc(33.333% - 24px)' },
                  minWidth: { xs: '100%', md: '180px' },
                  maxWidth: { md: '350px' },
                  display: 'flex',
                }}
              >
                <PlanCard isPrimary={plan.popular}>
                  <PlanHeader>
                    <PlanTitle>{plan.name}</PlanTitle>
                    {plan.popular && <PopularBadge label="Popular" size="small" />}
                  </PlanHeader>
                  <PlanContent>
                    <Box sx={{ mb: 3 }}>
                      <Typography
                        variant="caption"
                        sx={{ color: '#535862', display: 'block', mb: 1 }}
                      >
                        {plan.units}
                      </Typography>
                      <Box display="flex" alignItems="center" mb={1}>
                        <PlanPrice>${plan.price}</PlanPrice>
                        {plan.price > 0 && <PlanPeriod>/mo</PlanPeriod>}
                      </Box>
                      {plan.price > 0 && (
                        <Typography
                          variant="caption"
                          sx={{ color: '#535862', display: 'block', mb: 1 }}
                        >
                          ${plan.unitPrice}/unit
                        </Typography>
                      )}
                      <PlanDescription>{plan.description}</PlanDescription>
                    </Box>
                    <Stack spacing={1.5} sx={{ mt: 'auto' }}>
                      <RouterLink to="/signup" style={{ textDecoration: 'none', width: '100%' }}>
                        <GetStartedButton variant="contained" size="medium">
                          Get started
                        </GetStartedButton>
                      </RouterLink>
                    </Stack>
                  </PlanContent>
                </PlanCard>
              </Box>
            ))}
          </Stack>

          {/* Bottom row - remaining 3 plans (2 regular plans + Enterprise) */}
          <Stack
            direction={{ xs: 'column', md: 'row' }}
            spacing={3}
            sx={{
              justifyContent: 'center',
            }}
          >
            {plans.slice(3).map(plan => (
              <Box
                key={plan.name}
                sx={{
                  flex: { xs: '1 1 100%', md: '1 0 calc(33.333% - 24px)' },
                  minWidth: { xs: '100%', md: '180px' },
                  maxWidth: { md: '350px' },
                  display: 'flex',
                }}
              >
                <PlanCard isPrimary={plan.popular}>
                  <PlanHeader>
                    <PlanTitle>{plan.name}</PlanTitle>
                    {plan.popular && <PopularBadge label="Popular" size="small" />}
                  </PlanHeader>
                  <PlanContent>
                    <Box sx={{ mb: 3 }}>
                      <Typography
                        variant="caption"
                        sx={{ color: '#535862', display: 'block', mb: 1 }}
                      >
                        {plan.units}
                      </Typography>
                      <Box display="flex" alignItems="center" mb={1}>
                        <PlanPrice>${plan.price}</PlanPrice>
                        {plan.price > 0 && <PlanPeriod>/mo</PlanPeriod>}
                      </Box>
                      {plan.price > 0 && (
                        <Typography
                          variant="caption"
                          sx={{ color: '#535862', display: 'block', mb: 1 }}
                        >
                          ${plan.unitPrice}/unit
                        </Typography>
                      )}
                      <PlanDescription>{plan.description}</PlanDescription>
                    </Box>
                    <Stack spacing={1.5} sx={{ mt: 'auto' }}>
                      <RouterLink to="/signup" style={{ textDecoration: 'none', width: '100%' }}>
                        <GetStartedButton variant="contained" size="medium">
                          Get started
                        </GetStartedButton>
                      </RouterLink>
                    </Stack>
                  </PlanContent>
                </PlanCard>
              </Box>
            ))}
            <Box
              sx={{
                flex: { xs: '1 1 100%', md: '1 0 calc(33.333% - 24px)' },
                minWidth: { xs: '100%', md: '180px' },
                maxWidth: { md: '350px' },
                display: 'flex',
              }}
            >
              <PlanCard>
                <PlanHeader>
                  <PlanTitle>Enterprise</PlanTitle>
                </PlanHeader>
                <PlanContent>
                  <Box sx={{ mb: 3 }}>
                    <Typography
                      variant="caption"
                      sx={{ color: '#535862', display: 'block', mb: 1 }}
                    >
                      100+ Units
                    </Typography>
                    <Box display="flex" alignItems="center" mb={1}>
                      <Typography variant="h5" sx={{ fontWeight: 600 }}>
                        Custom pricing
                      </Typography>
                    </Box>
                    <PlanDescription>
                      Tailored solutions for large property portfolios with dedicated support.
                    </PlanDescription>
                  </Box>
                  <Stack spacing={1.5} sx={{ mt: 'auto' }}>
                    <ChatToSalesButton variant="outlined" size="medium" href="mailto:hello@terr.ai">
                      Contact sales
                    </ChatToSalesButton>
                  </Stack>
                </PlanContent>
              </PlanCard>
            </Box>
          </Stack>
        </Stack>

        {/* Comparison Table */}
        <ComparisonTable>
          <Table sx={{ minWidth: 850 }}>
            <TableHead>
              <TableRow>
                <StyledTableCell isCategory />
                {plans.map(plan => (
                  <StyledTableCell key={plan.name} align="center">
                    {plan.name}
                  </StyledTableCell>
                ))}
                <StyledTableCell align="center">Enterprise</StyledTableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {/* Basic Features Section */}
              <TableRow>
                <StyledTableCell isCategory>Basic Features</StyledTableCell>
                <StyledTableCell align="center" />
                <StyledTableCell align="center" />
                <StyledTableCell align="center" />
                <StyledTableCell align="center" />
                <StyledTableCell align="center" />
                <StyledTableCell align="center" />
              </TableRow>
              <TableRow>
                <StyledTableCell isFeature>Users</StyledTableCell>
                <StyledTableCell align="center">{plans[0].features.users}</StyledTableCell>
                <StyledTableCell align="center">{plans[1].features.users}</StyledTableCell>
                <StyledTableCell align="center">{plans[2].features.users}</StyledTableCell>
                <StyledTableCell align="center">{plans[3].features.users}</StyledTableCell>
                <StyledTableCell align="center">{plans[4].features.users}</StyledTableCell>
                <StyledTableCell align="center">Unlimited</StyledTableCell>
              </TableRow>
              <TableRow>
                <StyledTableCell isFeature>Storage Limit</StyledTableCell>
                <StyledTableCell align="center">{plans[0].features.storage}</StyledTableCell>
                <StyledTableCell align="center">{plans[1].features.storage}</StyledTableCell>
                <StyledTableCell align="center">{plans[2].features.storage}</StyledTableCell>
                <StyledTableCell align="center">{plans[3].features.storage}</StyledTableCell>
                <StyledTableCell align="center">{plans[4].features.storage}</StyledTableCell>
                <StyledTableCell align="center">Unlimited</StyledTableCell>
              </TableRow>
              <TableRow>
                <StyledTableCell isFeature>Support</StyledTableCell>
                <StyledTableCell align="center">
                  {plans[0].features.support ? <CellCheck /> : <CellMinus />}
                </StyledTableCell>
                <StyledTableCell align="center">
                  {plans[1].features.support ? <CellCheck /> : <CellMinus />}
                </StyledTableCell>
                <StyledTableCell align="center">
                  {plans[2].features.support ? <CellCheck /> : <CellMinus />}
                </StyledTableCell>
                <StyledTableCell align="center">
                  {plans[3].features.support ? <CellCheck /> : <CellMinus />}
                </StyledTableCell>
                <StyledTableCell align="center">
                  {plans[4].features.support ? <CellCheck /> : <CellMinus />}
                </StyledTableCell>
                <StyledTableCell align="center">
                  <CellCheck />
                </StyledTableCell>
              </TableRow>

              {/* Reporting and Analytics */}
              <TableRow>
                <StyledTableCell isCategory>Reporting and Analytics</StyledTableCell>
                <StyledTableCell align="center" />
                <StyledTableCell align="center" />
                <StyledTableCell align="center" />
                <StyledTableCell align="center" />
                <StyledTableCell align="center" />
                <StyledTableCell align="center" />
              </TableRow>
              <TableRow>
                <StyledTableCell isFeature>Analytics</StyledTableCell>
                <StyledTableCell align="center">{plans[0].features.analytics}</StyledTableCell>
                <StyledTableCell align="center">{plans[1].features.analytics}</StyledTableCell>
                <StyledTableCell align="center">{plans[2].features.analytics}</StyledTableCell>
                <StyledTableCell align="center">{plans[3].features.analytics}</StyledTableCell>
                <StyledTableCell align="center">{plans[4].features.analytics}</StyledTableCell>
                <StyledTableCell align="center">Premium</StyledTableCell>
              </TableRow>
              <TableRow>
                <StyledTableCell isFeature>Export Reports</StyledTableCell>
                <StyledTableCell align="center">
                  <CellMinus />
                </StyledTableCell>
                <StyledTableCell align="center">
                  <CellMinus />
                </StyledTableCell>
                <StyledTableCell align="center">
                  <CellMinus />
                </StyledTableCell>
                <StyledTableCell align="center">
                  <CellCheck />
                </StyledTableCell>
                <StyledTableCell align="center">
                  <CellCheck />
                </StyledTableCell>
                <StyledTableCell align="center">
                  <CellCheck />
                </StyledTableCell>
              </TableRow>

              {/* Communications Section */}
              <TableRow>
                <StyledTableCell isCategory>Communications</StyledTableCell>
                <StyledTableCell align="center" />
                <StyledTableCell align="center" />
                <StyledTableCell align="center" />
                <StyledTableCell align="center" />
                <StyledTableCell align="center" />
                <StyledTableCell align="center" />
              </TableRow>
              <TableRow>
                <StyledTableCell isFeature>Inbound Email Messages</StyledTableCell>
                <StyledTableCell align="center">
                  {plans[0].features.inboundMessages ? <CellCheck /> : <CellMinus />}
                </StyledTableCell>
                <StyledTableCell align="center">
                  {plans[1].features.inboundMessages ? <CellCheck /> : <CellMinus />}
                </StyledTableCell>
                <StyledTableCell align="center">
                  {plans[2].features.inboundMessages ? <CellCheck /> : <CellMinus />}
                </StyledTableCell>
                <StyledTableCell align="center">
                  {plans[3].features.inboundMessages ? <CellCheck /> : <CellMinus />}
                </StyledTableCell>
                <StyledTableCell align="center">
                  {plans[4].features.inboundMessages ? <CellCheck /> : <CellMinus />}
                </StyledTableCell>
                <StyledTableCell align="center">
                  <CellCheck />
                </StyledTableCell>
              </TableRow>
              <TableRow>
                <StyledTableCell isFeature>Inbound SMS Messages</StyledTableCell>
                <StyledTableCell align="center">
                  {plans[0].features.inboundSMS ? <CellCheck /> : <CellMinus />}
                </StyledTableCell>
                <StyledTableCell align="center">
                  {plans[1].features.inboundSMS ? <CellCheck /> : <CellMinus />}
                </StyledTableCell>
                <StyledTableCell align="center">
                  {plans[2].features.inboundSMS ? <CellCheck /> : <CellMinus />}
                </StyledTableCell>
                <StyledTableCell align="center">
                  {plans[3].features.inboundSMS ? <CellCheck /> : <CellMinus />}
                </StyledTableCell>
                <StyledTableCell align="center">
                  {plans[4].features.inboundSMS ? <CellCheck /> : <CellMinus />}
                </StyledTableCell>
                <StyledTableCell align="center">
                  <CellCheck />
                </StyledTableCell>
              </TableRow>
              <TableRow>
                <StyledTableCell isFeature>Inbound Calls</StyledTableCell>
                <StyledTableCell align="center">
                  {plans[0].features.inboundCalls ? <CellCheck /> : <CellMinus />}
                </StyledTableCell>
                <StyledTableCell align="center">
                  {plans[1].features.inboundCalls ? <CellCheck /> : <CellMinus />}
                </StyledTableCell>
                <StyledTableCell align="center">
                  {plans[2].features.inboundCalls ? <CellCheck /> : <CellMinus />}
                </StyledTableCell>
                <StyledTableCell align="center">
                  {plans[3].features.inboundCalls ? <CellCheck /> : <CellMinus />}
                </StyledTableCell>
                <StyledTableCell align="center">
                  {plans[4].features.inboundCalls ? <CellCheck /> : <CellMinus />}
                </StyledTableCell>
                <StyledTableCell align="center">
                  <CellCheck />
                </StyledTableCell>
              </TableRow>

              {/* Operations Hub Section */}
              <TableRow>
                <StyledTableCell isCategory>Operations Hub</StyledTableCell>
                <StyledTableCell align="center" />
                <StyledTableCell align="center" />
                <StyledTableCell align="center" />
                <StyledTableCell align="center" />
                <StyledTableCell align="center" />
                <StyledTableCell align="center" />
              </TableRow>
              <TableRow>
                <StyledTableCell isFeature>Workflows</StyledTableCell>
                <StyledTableCell align="center">
                  {plans[0].features.workflows ? <CellCheck /> : <CellMinus />}
                </StyledTableCell>
                <StyledTableCell align="center">
                  {plans[1].features.workflows ? <CellCheck /> : <CellMinus />}
                </StyledTableCell>
                <StyledTableCell align="center">
                  {plans[2].features.workflows ? <CellCheck /> : <CellMinus />}
                </StyledTableCell>
                <StyledTableCell align="center">
                  {plans[3].features.workflows ? <CellCheck /> : <CellMinus />}
                </StyledTableCell>
                <StyledTableCell align="center">
                  {plans[4].features.workflows ? <CellCheck /> : <CellMinus />}
                </StyledTableCell>
                <StyledTableCell align="center">
                  <CellCheck />
                </StyledTableCell>
              </TableRow>
              <TableRow>
                <StyledTableCell isFeature>Property Invoices</StyledTableCell>
                <StyledTableCell align="center">
                  {plans[0].features.propertyInvoices ? <CellCheck /> : <CellMinus />}
                </StyledTableCell>
                <StyledTableCell align="center">
                  {plans[1].features.propertyInvoices ? <CellCheck /> : <CellMinus />}
                </StyledTableCell>
                <StyledTableCell align="center">
                  {plans[2].features.propertyInvoices ? <CellCheck /> : <CellMinus />}
                </StyledTableCell>
                <StyledTableCell align="center">
                  {plans[3].features.propertyInvoices ? <CellCheck /> : <CellMinus />}
                </StyledTableCell>
                <StyledTableCell align="center">
                  {plans[4].features.propertyInvoices ? <CellCheck /> : <CellMinus />}
                </StyledTableCell>
                <StyledTableCell align="center">
                  <CellCheck />
                </StyledTableCell>
              </TableRow>
              <TableRow>
                <StyledTableCell isFeature>Property Calendar</StyledTableCell>
                <StyledTableCell align="center">
                  {plans[0].features.propertyCalendar ? <CellCheck /> : <CellMinus />}
                </StyledTableCell>
                <StyledTableCell align="center">
                  {plans[1].features.propertyCalendar ? <CellCheck /> : <CellMinus />}
                </StyledTableCell>
                <StyledTableCell align="center">
                  {plans[2].features.propertyCalendar ? <CellCheck /> : <CellMinus />}
                </StyledTableCell>
                <StyledTableCell align="center">
                  {plans[3].features.propertyCalendar ? <CellCheck /> : <CellMinus />}
                </StyledTableCell>
                <StyledTableCell align="center">
                  {plans[4].features.propertyCalendar ? <CellCheck /> : <CellMinus />}
                </StyledTableCell>
                <StyledTableCell align="center">
                  <CellCheck />
                </StyledTableCell>
              </TableRow>
              <TableRow>
                <StyledTableCell isFeature>Maintenance Requests</StyledTableCell>
                <StyledTableCell align="center">
                  {plans[0].features.maintenanceRequests ? <CellCheck /> : <CellMinus />}
                </StyledTableCell>
                <StyledTableCell align="center">
                  {plans[1].features.maintenanceRequests ? <CellCheck /> : <CellMinus />}
                </StyledTableCell>
                <StyledTableCell align="center">
                  {plans[2].features.maintenanceRequests ? <CellCheck /> : <CellMinus />}
                </StyledTableCell>
                <StyledTableCell align="center">
                  {plans[3].features.maintenanceRequests ? <CellCheck /> : <CellMinus />}
                </StyledTableCell>
                <StyledTableCell align="center">
                  {plans[4].features.maintenanceRequests ? <CellCheck /> : <CellMinus />}
                </StyledTableCell>
                <StyledTableCell align="center">
                  <CellCheck />
                </StyledTableCell>
              </TableRow>
              <TableRow>
                <StyledTableCell isFeature>Document Vault</StyledTableCell>
                <StyledTableCell align="center">
                  {plans[0].features.documentVault ? <CellCheck /> : <CellMinus />}
                </StyledTableCell>
                <StyledTableCell align="center">
                  {plans[1].features.documentVault ? <CellCheck /> : <CellMinus />}
                </StyledTableCell>
                <StyledTableCell align="center">
                  {plans[2].features.documentVault ? <CellCheck /> : <CellMinus />}
                </StyledTableCell>
                <StyledTableCell align="center">
                  {plans[3].features.documentVault ? <CellCheck /> : <CellMinus />}
                </StyledTableCell>
                <StyledTableCell align="center">
                  {plans[4].features.documentVault ? <CellCheck /> : <CellMinus />}
                </StyledTableCell>
                <StyledTableCell align="center">
                  <CellCheck />
                </StyledTableCell>
              </TableRow>
            </TableBody>
          </Table>
        </ComparisonTable>
      </Container>
    </SectionWrapper>
  );
};

export default PricingPlansSection;
